import { renderPortStickerQrCanvas } from './portStickerQr';

/**
 * Layout measured from public/station-port-sticker-template.png (820×1000, Group 493).
 * Smooth dark header fade into template purple glow around the QR card.
 */
const STICKER_LAYOUT = {
  width: 820,
  height: 1000,
  outerRadius: 52,
  eraseColor: '#101010',
  textColor: '#ffffff',
  portFontSize: 96,
  topFadeEnd: 430,
  qrCard: { x: 180, y: 394, w: 461, h: 470, radius: 28 },
  qrPadding: 28,
  glowZone: { x: 160, y: 136, w: 510, h: 258 },
  portLabelClear: { x: 80, y: 128, w: 660, h: 82 },
};

const PRINT_ORDER_MESSAGE = [
  'Вітаю! Хочу замовити матовий латексний друк на оракалі',
  '',
  'Наліпки будуть на зарядних станціях на вулиці під дощем та сонцем',
].join('\n');

const PRINT_ORDER_URL = `https://t.me/kpiprint1?text=${encodeURIComponent(PRINT_ORDER_MESSAGE)}`;

function getTemplateUrl() {
  const base = (typeof process !== 'undefined' && process.env.PUBLIC_URL) || '';
  return `${base}/station-port-sticker-template.png`;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Sticker template load failed'));
    img.src = url;
  });
}

function fillRoundedRect(ctx, x, y, width, height, radius, fillStyle) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
  if (fillStyle) {
    ctx.fillStyle = fillStyle;
    ctx.fill();
  }
}

function applyOuterRoundedMask(canvas, radius) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.globalCompositeOperation = 'destination-in';
  fillRoundedRect(ctx, 0, 0, canvas.width, canvas.height, radius, '#000');
  ctx.globalCompositeOperation = 'source-over';
}

function fitCenteredFont(ctx, lines, { fontSize, fontWeight, maxWidth }) {
  let size = fontSize;
  const family = "Arial, 'Fira Sans', system-ui, sans-serif";
  const applyFont = nextSize => {
    ctx.font = `${fontWeight} ${nextSize}px ${family}`;
  };

  applyFont(size);
  if (!maxWidth) return size;

  const widest = () => lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
  while (size > 14 && widest() > maxWidth) {
    size -= 1;
    applyFont(size);
  }
  return size;
}

function lineGapFor(size, lineCount) {
  const ratio = lineCount >= 5 ? 1.05 : lineCount === 2 ? 1.28 : 1.15;
  return Math.round(size * ratio);
}

function drawCenteredText(
  ctx,
  text,
  centerX,
  centerY,
  { fontSize, fontWeight, color, maxWidth, maxBlockHeight, blockTop }
) {
  const lines = String(text || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  if (!lines.length) return;

  let size = fitCenteredFont(ctx, lines, { fontSize, fontWeight, maxWidth });
  const family = "Arial, 'Fira Sans', system-ui, sans-serif";
  if (lines.length > 1 && maxBlockHeight) {
    while (size > 28) {
      const gap = lineGapFor(size, lines.length);
      const block = size + (lines.length - 1) * gap;
      if (block <= maxBlockHeight) break;
      size -= 1;
      ctx.font = `${fontWeight} ${size}px ${family}`;
    }
  }

  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (lines.length === 1) {
    ctx.fillText(lines[0], centerX, centerY);
    return;
  }

  const lineGap = lineGapFor(size, lines.length);
  const firstY = blockTop != null ? blockTop + size / 2 : centerY - ((lines.length - 1) * lineGap) / 2;
  lines.forEach((line, index) => {
    ctx.fillText(line, centerX, firstY + index * lineGap);
  });
}

function drawPortListBesideQr(ctx, lines, qrCard, { fontSize, fontWeight, color, leftX = 28, rightLimit }) {
  if (!lines.length) return;
  const family = "Arial, 'Fira Sans', system-ui, sans-serif";
  const maxWidth = Math.max(48, (rightLimit ?? qrCard.x - 28) - leftX);
  let size = fontSize;
  const applyFont = nextSize => {
    ctx.font = `${fontWeight} ${nextSize}px ${family}`;
  };
  applyFont(size);
  const widest = () => lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
  while (size > 28 && widest() > maxWidth) {
    size -= 1;
    applyFont(size);
  }

  const lineGap = Math.round(size * 1.35);
  const centerY = qrCard.y + qrCard.h / 2;
  const firstY = centerY - ((lines.length - 1) * lineGap) / 2;
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  lines.forEach((line, index) => {
    ctx.fillText(line, leftX, firstY + index * lineGap);
  });
}

function getQrPlacement(qrCard, qrPadding) {
  const innerW = qrCard.w - qrPadding * 2;
  const innerH = qrCard.h - qrPadding * 2;
  const qrSize = Math.min(innerW, innerH);
  const x = qrCard.x + (qrCard.w - qrSize) / 2;
  const y = qrCard.y + (qrCard.h - qrSize) / 2;
  return { x, y, size: qrSize };
}

function eraseSampleQrFromGlowZone(ctx, zone) {
  const { x, y, w, h } = zone;
  const imageData = ctx.getImageData(x, y, w, h);
  const { data } = imageData;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const avgLum = (r + g + b) / 3;
    const isModule = (r < 32 && g < 32 && b < 32) || (r > 235 && g > 235 && b > 235) || avgLum > 78;
    if (!isModule) continue;
    data[i] = 16;
    data[i + 1] = 16;
    data[i + 2] = 17;
  }

  ctx.putImageData(imageData, x, y);
}

function clearBakedPortLabel(ctx, zone) {
  const { x, y, w, h } = zone;
  const imageData = ctx.getImageData(x, y, w, h);
  const { data } = imageData;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const avgLum = (r + g + b) / 3;
    if (avgLum < 44) continue;
    data[i] = 16;
    data[i + 1] = 16;
    data[i + 2] = 17;
  }

  ctx.putImageData(imageData, x, y);
}

/** Opaque dark header that fades out smoothly before the QR card glow. */
function drawSmoothTopFade(ctx, { width, topFadeEnd, eraseColor }) {
  const gradient = ctx.createLinearGradient(0, 0, 0, topFadeEnd);
  gradient.addColorStop(0, eraseColor);
  gradient.addColorStop(0.62, eraseColor);
  gradient.addColorStop(0.82, 'rgba(16, 16, 17, 0.72)');
  gradient.addColorStop(0.94, 'rgba(16, 16, 17, 0.22)');
  gradient.addColorStop(1, 'rgba(16, 16, 17, 0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, topFadeEnd);
}

export const START_PAGE_STICKER_PRINT_ORDER_URL = PRINT_ORDER_URL;

export async function renderPortStickerCanvas({ stationNumber, shareUrl, portTitle }) {
  if (typeof window === 'undefined' || !stationNumber || !shareUrl) {
    throw new Error('Sticker render unavailable');
  }

  const template = await loadImage(getTemplateUrl());
  const canvas = document.createElement('canvas');
  canvas.width = STICKER_LAYOUT.width;
  canvas.height = STICKER_LAYOUT.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas unavailable');
  }

  const { qrCard, qrPadding, eraseColor, glowZone, portLabelClear } = STICKER_LAYOUT;
  const centerX = STICKER_LAYOUT.width / 2;
  const portText = portTitle || `ПОРТ # ${stationNumber}`;
  const stickerLines = portText
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  const portsBesideQr = stickerLines.length > 4;
  const portLineCount = stickerLines.length;

  ctx.drawImage(template, 0, 0, STICKER_LAYOUT.width, STICKER_LAYOUT.height);

  eraseSampleQrFromGlowZone(ctx, glowZone);
  clearBakedPortLabel(ctx, portLabelClear);
  drawSmoothTopFade(ctx, STICKER_LAYOUT);

  fillRoundedRect(ctx, qrCard.x, qrCard.y, qrCard.w, qrCard.h, qrCard.radius, eraseColor);
  fillRoundedRect(ctx, qrCard.x, qrCard.y, qrCard.w, qrCard.h, qrCard.radius, '#ffffff');

  const qrPlacement = getQrPlacement(qrCard, qrPadding);
  const qrCanvas = renderPortStickerQrCanvas(shareUrl, qrPlacement.size * 2, {
    margin: 2,
    color: '#111111',
  });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(qrCanvas, qrPlacement.x, qrPlacement.y, qrPlacement.size, qrPlacement.size);

  const headingText = portsBesideQr ? stickerLines[0] : portText;
  const headingLineCount = portsBesideQr ? 1 : portLineCount;

  const headerTop = headingLineCount === 2 ? 168 : headingLineCount === 3 ? 112 : 56;
  const headerBottom = qrCard.y - (headingLineCount === 2 ? 64 : 24);
  const headerCenter = headingLineCount > 1 ? (headerTop + headerBottom) / 2 : 190;
  drawCenteredText(ctx, headingText, centerX, headerCenter, {
    fontSize: headingLineCount === 3 ? 70 : headingLineCount === 2 || portsBesideQr ? 78 : STICKER_LAYOUT.portFontSize,
    fontWeight: '700',
    color: STICKER_LAYOUT.textColor,
    maxWidth: STICKER_LAYOUT.width - 48,
    maxBlockHeight: headingLineCount > 2 ? headerBottom - headerTop : undefined,
    blockTop: headingLineCount === 2 || headingLineCount === 3 ? headerTop : undefined,
  });

  if (portsBesideQr) {
    drawPortListBesideQr(ctx, stickerLines.slice(1), qrCard, {
      fontSize: 44,
      fontWeight: '700',
      color: STICKER_LAYOUT.textColor,
      leftX: 22,
      rightLimit: qrCard.x - 28,
    });
  }

  applyOuterRoundedMask(canvas, STICKER_LAYOUT.outerRadius);

  return canvas;
}

export async function createPortStickerPreviewDataUrl(params) {
  const canvas = await renderPortStickerCanvas(params);
  return canvas.toDataURL('image/png');
}

export async function downloadPortStickerPng(params) {
  const canvas = await renderPortStickerCanvas(params);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      result => {
        if (result) resolve(result);
        else reject(new Error('PNG export failed'));
      },
      'image/png',
      1
    );
  });

  triggerBlobDownload(blob, `port-${params.stationNumber}-sticker.png`);
}
